// Real, source-timestamped audio peaks for Refine. Unlike decodeAudioData,
// Mediabunny's ranged URL source doesn't load a multi-gigabyte original into RAM.
// Only the requested Assembly passage or trim context is decoded.
const RATE=80;
const MAX_CACHE=64;
const MAX_WORKERS=2;
const cache=new Map();
const queue=[];
let running=0;

export function peaksFromAudioBuffers(buffers,start,end,rate=RATE){
 const length=Math.max(1,Math.ceil((end-start)*rate));
 const peaks=new Float32Array(length);
 for(const {buffer,timestamp} of buffers){
  for(let channel=0;channel<buffer.numberOfChannels;channel++){
   const data=buffer.getChannelData(channel);
   // Read each original audio sample; do not synthesize amplitudes from words.
   for(let i=0;i<data.length;i++){
    const sampleTime=timestamp+i/buffer.sampleRate;
    const bucket=Math.floor((sampleTime-start)*rate);
    if(bucket>=0&&bucket<length)peaks[bucket]=Math.max(peaks[bucket],Math.abs(data[i]));
   }
  }
 }
 return {peaks,rate,offset:start};
}
async function decodeRange(url,start,end){
 const {Input,ALL_FORMATS,UrlSource,AudioBufferSink}=await import('mediabunny');
 const input=new Input({formats:ALL_FORMATS,source:new UrlSource(url,{maxCacheSize:4*1024*1024})});
 try{
  const track=await input.getPrimaryAudioTrack();
  if(!track)throw new Error('Source has no audio track');
  const sink=new AudioBufferSink(track);
  const peaks=new Float32Array(Math.max(1,Math.ceil((end-start)*RATE)));
  for await(const {buffer,timestamp} of sink.buffers(start,end)){
   for(let channel=0;channel<buffer.numberOfChannels;channel++){
    const data=buffer.getChannelData(channel);
    for(let i=0;i<data.length;i++){
     const bucket=Math.floor((timestamp+i/buffer.sampleRate-start)*RATE);
     if(bucket>=0&&bucket<peaks.length)peaks[bucket]=Math.max(peaks[bucket],Math.abs(data[i]));
    }
   }
  }
  return {peaks,rate:RATE,offset:start};
 }finally{await input.dispose?.();}
}
function drain(){
 while(running<MAX_WORKERS&&queue.length){
  const item=queue.shift();
  running++;
  decodeRange(item.url,item.start,item.end).then(item.resolve,item.reject)
   .finally(()=>{running--;drain();});
 }
}
export function getRangeWaveform(url,start,end){
 if(!url||!Number.isFinite(start)||!Number.isFinite(end)||start<0||end<=start)
  return Promise.reject(new Error('Invalid audio waveform range'));
 // 1/80s precision is more than sufficient for the visual layer. Frame-accurate
 // trim values continue to come from original source timestamps, never peaks.
 const a=Math.floor(start*RATE)/RATE,b=Math.ceil(end*RATE)/RATE;
 const key=url+'|'+a+'|'+b;
 if(cache.has(key))return cache.get(key);
 const result=new Promise((resolve,reject)=>{queue.push({url,start:a,end:b,resolve,reject});drain();});
 cache.set(key,result);
 result.catch(()=>{if(cache.get(key)===result)cache.delete(key);});
 while(cache.size>MAX_CACHE)cache.delete(cache.keys().next().value);
 return result;
}
